/**
 * THIS FILE IS AUTO-GENERATED
 * DO NOT EDIT DIRECTLY
 * 
 * Generated from real chess tactics examples using src/data/chess_tactics/extractTactics.ts
 */

/**
 * Tactical pattern definition
 */
export interface TacticalPattern {
  fen: string;             // Starting position
  moves: string[];         // Sequence of moves
  description: string;     // Description of the tactical motif
}

/**
 * Collection of predefined tactical patterns organized by theme
 */
export const TACTICAL_PATTERNS: Record<string, TacticalPattern[]> = {
  // Fork patterns (one piece attacks multiple targets)
  fork: [
    {
      fen: '6r1/bpp2k1n/p2p3B/P3p3/3nP3/2N2r2/1P6/R3K3 b Q - 1 26',
      moves: ['Nc2+', 'Ke2', 'Nxa1', 'Kxf3', 'Rg6', 'Be3'],
      description: 'Knight on c2 forks: King on e1, Rook on a1. Best capture: Rook (value 5).'
    },
    {
      fen: '2N5/Bp6/p1kp2r1/P1n1p2n/4P3/5K2/1P6/8 w - - 7 36',
      moves: ['Ne7+', 'Kd7', 'Nxg6', 'Ke6', 'Bxc5', 'dxc5'],
      description: 'Knight on e7 forks: Rook on g6, King on c6. Best capture: Rook (value 5).'
    },
    {
      fen: '2kr4/1p1qppb1/p2pb1p1/P1pN3r/2B1PP1p/2PP1Q2/1P1B3K/R5R1 w - - 0 22',
      moves: ['Nb6+', 'Kc7', 'Nxd7', 'Kxd7', 'Bxe6+', 'fxe6'],
      description: 'Knight on b6 forks: King on c8, Queen on d7. Best capture: Queen (value 9).'
    },
  ],

  // Pin patterns (piece immobilized due to piece of higher value behind it)
  pin: [
    {
      fen: '2r2rk1/6pp/5p2/4p3/1QRpP3/P4bP1/5P1P/6K1 w - - 0 28',
      moves: ['Rxc8', 'Rxc8', 'Qb3+', 'Kf8', 'Qxf3', 'Rc1+'],
      description: 'Rook pin against King'
    },
    {
      fen: '2r2k2/6pp/5p2/4p3/3pP3/PQ3bP1/5P1P/6K1 w - - 2 30',
      moves: ['Qxf3', 'Rc1+', 'Kg2', 'Ra1', 'h4', 'Kf7'],
      description: 'Queen pin against King'
    },
    {
      fen: 'r2qkb1r/pp3ppp/1np1pn2/3p4/5P2/3PPN2/PPP1B1PP/R1BQ1RK1 b kq - 1 10',
      moves: ['Bc5', 'd4', 'Bd6', 'c3', 'c5', 'Qe1'],
      description: 'Bishop pin against King'
    },
  ],

  // Discovered Attack patterns (moving one piece reveals attack from another)
  discoveredAttack: [
    {
      fen: 'r1bqk2r/pp2bppp/2n1p3/2p5/2Ppn3/3P1NP1/PP2PPBP/R1BQ1RK1 w kq - 0 9',
      moves: ['dxe4', 'e5', 'a3', 'f6', 'Rb1', 'a5'],
      description: 'Pawn (d3-e4) captures Knight, discovering Queen on d1 attacks Pawn on d4'
    },
    {
      fen: 'r1b2rk1/1pq1b1pp/5p2/2p1p3/2PNP3/PQ4P1/3B1PBP/1R3RK1 b - - 0 17',
      moves: ['cxd4', 'Bb4', 'Re8', 'Rfd1', 'Bf8', 'Rd3'],
      description: 'Pawn (c5-d4) captures Knight, discovering Queen on c7 attacks Pawn on c4'
    },
    {
      fen: '2r1rbk1/2q3pp/4bp2/1p2p3/1BPpP3/PQ1R2P1/5P1P/2R2BK1 w - - 0 23',
      moves: ['Bxf8', 'bxc4', 'Qb4', 'Rxf8', 'Rf3', 'Bg4'],
      description: 'Bishop (b4-f8) captures Bishop, discovering Queen on b3 attacks Pawn on b5'
    },
  ],

  // Sacrifice patterns (intentional material loss for positional gain)
  sacrifice: [
    {
      fen: 'r2q1rk1/pb1nbppp/1p2pn2/3pN3/2pP1B2/2P3NP/PPB2PP1/R2QR1K1 b - - 1 14',
      moves: ['Nxe5', 'Bxe5', 'Ne4', 'Nxe4', 'dxe4', 'Bxe4'],
      description: 'Knight sacrifice to gain central control: Knight sacrificed to Bishop. Gained control of 2 central squares'
    },
    {
      fen: 'r4rk1/1b1pq1b1/n1p1pnpp/1p6/p2PN2P/P7/BPPBQ1P1/2KR2NR w - - 6 17',
      moves: ['Nc5', 'Nxc5', 'dxc5', 'Nd5', 'Bxd5', 'cxd5'],
      description: 'Knight sacrifice to gain central control: Knight sacrificed to Knight. Gained control of 2 central squares'
    },
    {
      fen: 'r4rk1/1b1pq1b1/2p1pnpp/1pP5/p6P/P7/BPPBQ1P1/2KR2NR b - - 0 18',
      moves: ['Nd5', 'Bxd5', 'cxd5', 'Nh3', 'e5', 'g4'],
      description: 'Knight sacrifice to gain central control: Knight sacrificed to Bishop. Gained control of 6 central squares'
    },
  ],

  // Checkmate patterns (forcing moves leading to checkmate)
  checkmate: [
    {
      fen: 'r3qk1r/pb6/1p3Q1p/8/2BPp3/4P3/PP4PP/R1B3K1 b - - 0 19',
      moves: ['Qf7', 'Qxf7#'],
      description: 'Classic queen and bishop mate with queen on f7. Supported by 1 piece(s). Achieved through a 1-move sequence.'
    },
    {
      fen: 'r4k1r/pb3q2/1p3Q1p/8/2BPp3/4P3/PP4PP/R1B3K1 w - - 1 20',
      moves: ['Qxf7#'],
      description: 'Classic queen and bishop mate with queen on f7. Supported by 1 piece(s).'
    },
    {
      fen: '5b1k/p7/3p1B1p/8/2B3Q1/2p5/P2r2PP/R5K1 b - - 1 30',
      moves: ['Kh7', 'Qg8#'],
      description: 'Classic queen and bishop mate with queen on g8. Supported by 1 piece(s). Achieved through a 1-move sequence.'
    },
  ],

};

/**
 * Gets all available tactical themes
 * @returns Array of available themes
 */
export function getAvailableTacticalThemes(): string[] {
  return Object.keys(TACTICAL_PATTERNS);
}

/**
 * Gets a description for a tactical theme
 * @param theme The tactical theme
 * @returns Description of the theme
 */
export function getTacticalThemeDescription(theme: string): string {
  switch(theme) {
    case 'fork':
      return 'A fork is a tactic where a single piece attacks two or more opponent pieces simultaneously.';
    case 'pin':
      return 'A pin is a tactic where a piece is prevented from moving because moving would expose a more valuable piece behind it.';
    case 'discoveredAttack':
      return 'A discovered attack occurs when a piece moves and reveals an attack from another piece that was behind it.';
    case 'sacrifice':
      return 'A sacrifice involves deliberately giving up material to gain a positional advantage or initiative.';
    case 'checkmate':
      return 'A checkmate is a position where the king is in check and there is no legal move to escape.';
    default:
      return 'A tactical combination of chess moves designed to gain an advantage.';
  }
}

export default {
  TACTICAL_PATTERNS,
  getAvailableTacticalThemes,
  getTacticalThemeDescription
}; 
